<template>
  <div class="modules-settings-vendors-list">
    <div class="nav-section">
      <a
        class="nav-back"
        @click="navToVendorTab()"
      >
        {{ $t('action.backToVendorTab') }}
      </a>
    </div>

    <label class="title">{{ $t('settings.user.vendor.all') }}</label>
    <div style="display: inline; vertical-align: middle;">
      <span>{{ $t('total') }}: {{ orderedItems.length }} / {{ recordCounts }}</span>
    </div>
    <div
      v-if="aclHelper.getSettingsUpdateAccess()"
      class="controls"
    >
      <AddVendor
        :companyList="items"
        :departmentList="departmentNames"
        parent="allVendors"
      />
      <DialogSlot
        id="settings-users-contractor-invite-admin"
        ref="invite-vendor-modal"
        :modalBtn="$t('action.invite')"
        :modalHeader="$t('action.sendInvitation')"
        :disableBtn="!orderedItems.length"
        :showCloseBtn="false"
      >
        <div slot="content">
          <div>
            <label class="required-flag">{{ $t('company.basic.companyName') }}</label>
            <HKLSelect
              :value="userToInviteId"
              class="align-center"
              :options="allUserHklOptions"
              :showSearch="true"
              placeholder=""
              @change="onInviteUserChange"
            />
          </div>
          <div class="new-multiselect">
            <label class="required-flag">{{ $t('company.basic.departmentName') }}</label>
            <HKLMultiSelect
              v-model="selectedDepartmentIds"
              class="align-center multi-value"
              :options="userToInviteDepartmentHklOptions"
              :showSearch="false"
              placeholder=""
            />
          </div>
          <div>
            <label class="required-flag">{{ $t('settings.user.vendor.username') }}</label>
            <span
              v-for="(user, index) in usersToInvite"
              :key="user.id"
            >
              {{ user.username }}{{ index === usersToInvite.length -1 ? '': ',' }}
            </span>
          </div>
        </div>
        <div slot="buttons">
          <button
            type="button"
            class="btn btn--orange"
            @click="closeModal()"
          >
            {{ $t('action.cancel') }}
          </button>
          <button
            type="button"
            class="btn btn--darkblue"
            :disabled="!userToInvite.username || usersToInvite.length === 0"
            @click="sendInvitation()"
          >
            {{ $t('action.invite') }}
          </button>
        </div>
      </DialogSlot>
    </div>
    <div class="tender-table">
      <table>
        <thead>
          <tr>
            <th
              v-for="(header, index) in itemHeaders"
              :key="index"
              :class="header.class"
              @click="sortBy(header.code)"
            >
              {{ $t(header.name) }} {{ orderbyAttr === header.code ? (reverse ? '&#9660;' : '&#9650;') : null }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <input
                id="settings-users-contractor-search-company"
                v-model="itemHeaders[0].filter"
                type="text"
                class="setting-users-filter-search"
              >
            </td>
            <td>
              <input
                id="settings-users-contractor-search-company"
                v-model="itemHeaders[1].filter"
                type="text"
                class="setting-users-filter-search"
              >
            </td>
            <td class="settings-filter align-center">
              <HKLSelect
                :value="itemHeaders[2].filter"
                class="align-center"
                settings-filter-row
                :options="departmentHklOptions"
                :showSearch="false"
                placeholder=""
                @change="val => { itemHeaders[2].filter = val }"
              />
            </td>
            <td>
              <input
                id="settings-users-contractor-search-username"
                v-model="itemHeaders[3].filter"
                type="text"
                class="setting-users-filter-search"
              >
            </td>
            <td class="settings-filter align-center">
              <HKLSelect
                :value="itemHeaders[4].filter"
                class="align-center"
                settings-filter-row
                :options="statusHklOptions"
                :showSearch="false"
                placeholder=""
                @change="val => { itemHeaders[4].filter = val }"
              />
            </td>
            <td colspan="2">
              <!--Placeholder td-->
            </td>
          </tr>
          <tr
            v-for="(item, index) in orderedItems"
            :key="index"
          >
            <td>
              <a @click="navToDetail(item.id)">{{ item.id }}</a>
            </td>
            <td>
              <a @click="navToDetail(item.id)">{{ item.name }}</a>
            </td>
            <td>
              {{ item.department }}
            </td>
            <td>
              <div class="align-left">
                {{ item.user.username }}
              </div>
            </td>
            <td>
              <div>{{ showStatus(item) | customStatusLabel }}</div>
            </td>
            <td>
              <div>{{ formatDate(item.user.lastLoginAt) }}</div>
            </td>
            <td>
              <div>{{ formatDate(item.user.invitedAt) }}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'

import DialogSlot from '@/controls/DialogSlot'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import AddVendor from '@/modules/settings/vendors/AddVendor'

import * as accountsRequest from '@/api/accounts/request'
import { getDepartments } from '@/api/departments/request'
import { contactTypes } from '@/api/users/consts'
import { aclHelper } from '@/utils/aclHelper'
import { EmailTypes } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import EventBus from '@/utils/eventBus'
import i18n from '@/utils/i18n'
import navigation from '@/utils/navigation'
import userHelper from '@/utils/userHelper'
import { getVendorPageDepartment } from './utils'

const defaultDepartmentFilter = ['All']

function getDefaultData () {
  const statusFilter = {
    ALL: 'All',
    INVITED: 'Invited',
    NEW: 'New',
  }

  return {
    aclHelper,
    statusFilter,
    departmentFilter: defaultDepartmentFilter,
    recordCounts: '',
    items: [],
    orderbyAttr: 'name',
    reverse: 0,
    userPerms: {},
    userStatus: {},
    userToInvite: '',
    departments: [],
    selectedDepartments: [],
    itemHeaders: [
      {
        code: 'id',
        name: 'tenancy.companyId',
        class: 'company_id',
        filter: '',
      },
      {
        code: 'name',
        name: 'company.basic.companyName',
        filter: '',
      },
      {
        code: 'department',
        name: 'company.basic.departmentName',
        class: 'department',
        filter: defaultDepartmentFilter[0],
      },
      {
        code: 'username',
        name: 'settings.user.vendor.username',
        class: 'email-align',
        filter: '',
      },
      {
        code: 'active',
        name: 'company.basic.status',
        class: 'status',
        filter: statusFilter.ALL,
      },
      {
        code: 'lastLoginAt',
        name: 'lastLogin',
        filter: '',
      },
      {
        code: 'invitedAt',
        name: 'invitedAt',
        filter: '',
      },
    ],
  }
}

export default {
  name: 'AllVendors',
  components: {
    AddVendor,
    DialogSlot,
    HKLSelect,
    HKLMultiSelect,
  },
  setup () {
    const customDepartmentsLabel = (option) => {
      return option === 'All' ? i18n.t(option.toLowerCase()) : i18n.t(option)
    }
    return {
      customDepartmentsLabel,
    }
  },
  data: getDefaultData,
  computed: {
    departmentHklOptions () {
      return this.departmentFilter.map((value) => ({
        name: this.customDepartmentsLabel(value),
        value,
      }))
    },
    statusHklOptions () {
      return Object.values(this.statusFilter).map((value) => ({
        name: this.$options.filters.customStatusLabel(value),
        value,
      }))
    },
    allUserHklOptions () {
      return this.allUsers.map((user) => ({
        name: user.companyName,
        value: user.companyId,
      }))
    },
    userToInviteId () {
      return lodash.get(this.userToInvite, 'companyId')
    },
    userToInviteDepartmentHklOptions () {
      return this.userToInviteDepartmentOptions.map((department) => ({
        name: department.name,
        value: department.id,
      }))
    },
    selectedDepartmentIds: {
      get () {
        return this.selectedDepartmentNames.map(({ id }) => id)
      },
      set (ids = []) {
        this.selectedDepartments = ids.map((id) => {
          const department = this.userToInviteDepartmentOptions.find(item => item.id === id) || {}
          return {
            name: department.shortName || department.name,
            id,
          }
        })
      },
    },
    orderedItems () {
      const filteredResult =
        this.items.filter(item => {
          item.lastLoginAt = lodash.get(item, 'user.lastLoginAt', 0)
          item.invitedAt = lodash.get(item, 'user.invitedAt', 0)
          item.username = lodash.get(item, 'user.username', '')
          const name = item.name
          return (item.id && name &&
            item.id.toString().toLowerCase().indexOf(this.itemHeaders[0].filter.toLowerCase()) !== -1 &&
            name.toString().toLowerCase().replace(/ /g, '').indexOf(this.itemHeaders[1].filter.toLowerCase().replace(/ /g, '')) !== -1)
        }).filter(item => {
          const departmentFilter = this.itemHeaders[2].filter
          const department = item.department
          return departmentFilter === this.departmentFilter[0] || (department && department === departmentFilter)
        }).filter(item => {
          const userFilter = this.itemHeaders[3].filter.toLowerCase().replace(/ /g, '')
          const username = lodash.get(item, 'user.username', '').toString().toLowerCase().replace(/ /g, '')
          return (username.indexOf(userFilter) !== -1)
        }).filter(item => {
          const statusFilter = this.itemHeaders[4].filter
          const status = this.showStatus(item)
          return statusFilter === this.statusFilter.ALL || (status && status === statusFilter)
        })
      const result = lodash.orderBy(filteredResult, (item) => {
        return ['lastLoginAt', 'invitedAt'].includes(this.orderbyAttr) ? new Date(item[this.orderbyAttr]) : item[this.orderbyAttr]
      })
      return this.reverse ? result.reverse() : result
    },
    allUsers () {
      if (!this.items.length) return []
      let allVendors = this.items.reduce((partial, item) => {
        if (Object.keys(item.user).length) {
          partial.push({
            ...item.user,
            companyName: item.name,
            companyId: item.id,
          })
        }
        return partial
      }, [])
      allVendors = lodash.uniqBy(allVendors, 'companyName')
      const result = lodash.orderBy(allVendors, this.orderbyAttr)
      return this.reverse ? result.reverse() : result
    },
    departmentNames () {
      const result = this.departments.map((department) => {
        const shortName = lodash.get(department, ['shortname'])
        const name = lodash.get(department, ['name'])
        const id = lodash.get(department, ['id'])
        return {
          shortName,
          name,
          id,
        }
      })
      return result
    },
    selectedDepartmentNames () {
      let result
      if (lodash.isEmpty(this.selectedDepartments)) {
        result = []
      } else {
        result = lodash.uniqBy(this.selectedDepartments, 'id')
      }
      return result
    },
    usersToInvite () {
      let result
      if (lodash.isEmpty(this.userToInvite) || lodash.isEmpty(this.selectedDepartments)) {
        result = []
      } else {
        const { externalId } = this.userToInvite
        const selectedUser = this.items.find(({ id }) => id === externalId)
        const members = lodash.get(selectedUser, ['users'])
        if (lodash.isArray(members) && members.length > 0) {
          const rawResult = this.selectedDepartmentNames.reduce((acc, department) => {
            const newAcc = acc
            const { id } = department
            const matchMember = members.find(({ departments, primary }) => {
              const flatDepartments = lodash.flatMap(departments, (d) => [d.id])
              return lodash.includes(flatDepartments, id) && primary
            })

            if (matchMember) {
              newAcc.push(matchMember)
            }
            return newAcc
          }, [])
          result = lodash.compact(lodash.uniqBy(rawResult, 'id'))
        }
      }
      return result
    },
    userToInviteDepartmentOptions () {
      const { externalId } = this.userToInvite
      const selectedUser = this.items.find(({ id }) => id === externalId)
      const members = lodash.get(selectedUser, ['users'], [])
      const primaryMembers = members.filter((member) => member.primary && member.departments.length > 0)
      const totalDepartments = primaryMembers.reduce((total, member) => {
        const departments = member.departments
        return [...total, ...departments]
      }, [])
      const departments = lodash.intersectionBy(this.departments, totalDepartments, 'id')
      const result = lodash.map(departments, ({ shortname, name, id }) => {
        return {
          shortName: shortname,
          name,
          id,
        }
      })
      return result
    },
  },
  async mounted () {
    await this.initPage()
  },
  methods: {
    sortBy (attr) {
      if (this.orderbyAttr === attr) {
        this.reverse = !this.reverse
      }
      this.orderbyAttr = attr
    },

    parseAllAccounts (companies = []) {
      const parsedCompanies = companies.reduce((acc, company) => {
        let admins = company.users.filter(user => user.primary)
        if (lodash.isEmpty(admins)) {
          admins = [{}]
        }
        const newCompanies = admins.reduce((total, admin) => {
          const newCompany = lodash.clone(company)
          newCompany.user = admin
          let newTotal = total
          if (admin.departments) {
            const splittedRecordsByDepartment = admin.departments.map((department) => {
              const result = {
                ...newCompany,
                department: department.name,
              }
              return result
            })
            newTotal = newTotal.concat(splittedRecordsByDepartment)
          } else {
            newTotal = newTotal.concat(newCompany)
          }
          return newTotal
        }, [])
        const newAcc = acc.concat(newCompanies)
        return newAcc
      }, [])
      return lodash.orderBy(parsedCompanies, this.orderByAttr)
    },
    async initPage () {
      const [items] = await accountsRequest.getAllContractorAccounts()
      this.items = this.parseAllAccounts(items)
      this.recordCounts = this.items.length
      const departments = await getDepartments()
      this.departments = departments
      const tempDepartmentFilter = await getVendorPageDepartment(() => departments)
      this.departmentFilter = [...defaultDepartmentFilter, ...tempDepartmentFilter.map(item => item.name)]
    },
    navToDetail (id) {
      if (id) {
        this.$router.push({
          name: 'vendor-detail',
          params: {
            id,
            navFrom: 'allVendors',
          },
        })
      }
    },
    formatDate (dateString) {
      return dateUtil.formatDate(dateString, true)
    },
    showStatus (item) {
      return lodash.get(item, 'user.enabled', false) ? this.statusFilter.INVITED : this.statusFilter.NEW
    },
    navToVendorTab () {
      navigation.toPage('setting-users')
      EventBus.emit('active-user-tab', 'contractor')
    },
    closeModal () {
      this.userToInvite = ''
      this.selectedDepartments = []
      this.$refs['invite-vendor-modal'].closeModal()
    },
    onInviteUserChange (companyId) {
      this.userToInvite = this.allUsers.find(user => user.companyId === companyId) || ''
      this.selectedDepartments = []
    },
    async sendInvitation () {
      const metaData = this.selectedDepartmentNames.map(dep => {
        const departmentId = dep.id
        const user = this.usersToInvite.find(u => u.departments.map(d => d.id).includes(departmentId))
        const adminContact = user.contacts.filter(contact => contact.type === contactTypes.NAME)[0]
        const adminContactName = adminContact && adminContact.value
        return {
          id: user.id,
          name: this.userToInvite.companyName,
          adminContact: userHelper.getUserName(user.username, adminContactName),
          template: EmailTypes.settings.createPassword,
          departmentId,
        }
      })

      await accountsRequest.inviteAccounts(metaData, 'inviteCreatedContractor')
      this.closeModal()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/settings/style';
@import '~styles/newMultiselect';

.modules-settings-vendors-list {
  padding: 0 24px;

  .title {
    font-weight: bold;
    margin-bottom: 10px;
  }
  table {
    thead th {
      &.company_id {
        width: 10%
      }
      &.department {
        width: 20%;
      }
      &.status {
        width: 10%;
      }
    }
  }
  tbody tr td {
    padding-left: 2px;
    a {
      padding-left: 0;
    }
  }
  th:first-child, td:first-child {
    width: 120px;
  }
  tbody tr td:nth-child(2) {
    text-align: left;
  }
  .align-center {
    margin: auto
  }
  .controls .dialog-slot {
    display: inline-block;
  }
  .disable-link {
    cursor: not-allowed;
    pointer-events: none;
  }

  & ::v-deep .settings-filter .hkl-select {
    max-width: 100%;
  }
}

label {
  &.required-flag {
    font-weight: bold;
  }
}
</style>
